import isEqual from "lodash-es/isEqual";
import type { Maybe } from "@/components/utils/typeHelpers";

export interface IHasDirtyCheck {
  isDirty(): boolean;
  setDirtyCheckpoint(): void;
}

export interface IModelObject<T = any> {
  validate(): string[];
  clone(): T;
  toProto(): any;
  toBinary(): Uint8Array;
  equals(other: T): boolean;
}

export type ValidationRule = {
  field: string;
  rule: (val: any) => boolean | string | string[];
}[];

export abstract class ModelObject<T extends IModelObject>
  implements IModelObject<T>
{
  abstract validationRules: ValidationRule;

  /**
   * Validates this object to ensure it's in a good state to be turned into a model object.
   */
  public validate(): string[] {
    let errors: string[] = [];
    for (let index = 0; index < this.validationRules.length; index++) {
      const { field, rule } = this.validationRules[index];
      const retVal = rule((this as any)[field]);
      if (typeof retVal === "string") {
        errors.push(retVal);
      } else if (Array.isArray(retVal)) {
        errors.push(...retVal);
      }
    }

    return errors;
  }

  public clone(): T {
    // @ts-ignore
    return new this.constructor(this.toProto());
  }

  abstract toProto(): any;

  abstract toBinary(): Uint8Array;

  public equals(other: Maybe<T>): boolean {
    return !!other && isEqual(this.toBinary(), other.toBinary());
  }
}

export abstract class BaseModelObject<T extends IModelObject>
  extends ModelObject<T>
  implements IModelObject<T>, IHasDirtyCheck
{
  private checkpoint?: string;

  /**
   * Whether this object has changed since the last checkpoint (or creation).
   */
  public isDirty(): boolean {
    return this.checkpoint !== JSON.stringify(this.toProto());
  }

  /**
   * Sets a checkpoint in the object which will make the current state "clean". All future changes will make the
   * object dirty.
   */
  public setDirtyCheckpoint(): void {
    this.checkpoint = JSON.stringify(this.toProto());
  }
}

/**
 * Wraps an array of IModelObjects as a type that implements IModelObject.
 * Array objects must be accessed via the 'items' property.
 */
export class ModelObjectArrayWrapper<T extends IModelObject<T> & IHasDirtyCheck>
  implements IModelObject<ModelObjectArrayWrapper<T>>
{
  public items: T[];
  private dirtyItems: T[];

  public constructor(items?: Array<T>) {
    this.items = items || [];
    this.dirtyItems = [];
  }

  validate(): string[] {
    return this.items.reduce(
      (valErrors: string[], item: IModelObject<T>) =>
        item.validate().concat(valErrors),
      []
    );
  }

  isDirty(): boolean {
    if (this.dirtyItems === null) {
      return true;
    } else {
      if (this.dirtyItems.length != this.items.length) {
        return true;
      }
      for (let idx = 0; idx < this.dirtyItems.length; idx++) {
        if (!this.dirtyItems[idx].equals(this.items[idx])) {
          return true;
        }
      }
    }
    return this.items.some((item) => item.isDirty());
  }

  clone(): ModelObjectArrayWrapper<T> {
    return new ModelObjectArrayWrapper(this.items.map((item) => item.clone()));
  }

  equals(other: ModelObjectArrayWrapper<T>): boolean {
    return isEqual(this.items, other.items);
  }

  setDirtyCheckpoint(): void {
    this.items.forEach((item) => item.setDirtyCheckpoint());
    this.dirtyItems = this.items.slice();
  }

  toProto(): any {
    return this.items.map((x) => x.toProto());
  }

  toBinary(): Uint8Array {
    return new Uint8Array();
  }
}
